// src/OnlineGame/Phasermmoscene.js
import Phaser from "phaser";

// import { saveCharacter, loadCharacter } from "./GameData"; // For future auto‑save/back‑end integration

class PhaserSlider extends Phaser.GameObjects.Container {
  constructor(scene, x, y, width, initialValue, callback) {
    super(scene, x, y);

    this.width = width;
    this.value = initialValue ?? 0.5;
    this.callback = callback;

    // Create the track
    this.track = scene.add.rectangle(0, 0, width, 8, 0x888888).setOrigin(0.5);
    this.track.setInteractive({ useHandCursor: true });
    this.add(this.track);

    // Create the knob
    this.knob = scene.add.circle(0, 0, 12, 0xffffff).setOrigin(0.5);
    this.knob.setInteractive({ useHandCursor: true, draggable: true });
    scene.input.setDraggable(this.knob);
    this.add(this.knob);

    // Set initial knob position
    this.updateKnobPosition();

    // Track interaction (pointerdown)
    this.track.on("pointerdown", (pointer) => {
      const localPos = this.getLocalPointerPosition(pointer);
      this.setSliderByLocalX(localPos.x);
    });

    // Knob dragging behavior
    this.knob.on("drag", (pointer) => {
      const localPos = this.getLocalPointerPosition(pointer);
      this.setSliderByLocalX(localPos.x);
    });

    scene.add.existing(this);
  }

  getLocalPointerPosition(pointer) {
    // Convert global pointer position to local position
    const camera = this.scene.cameras.main;
    const worldPoint = pointer.positionToCamera(camera);

    let parentX = 0, parentY = 0;
    if (this.parentContainer) {
      parentX = this.parentContainer.x;
      parentY = this.parentContainer.y;
    }

    const localX = worldPoint.x - this.x - parentX;
    const localY = worldPoint.y - this.y - parentY;
    return { x: localX, y: localY };
  }

  setSliderByLocalX(localX) {
    const halfWidth = this.width / 2;
    const clampedX = Phaser.Math.Clamp(localX, -halfWidth, halfWidth);
    this.knob.x = clampedX;
    this.value = (clampedX + halfWidth) / this.width;
    if (this.callback) this.callback(this.value);
  }

  updateKnobPosition() {
    this.knob.x = this.value * this.width - this.width / 2;
  }
}



export default class PhaserMMOScene extends Phaser.Scene {
  constructor() {
    super({ key: "PhaserMMO" });
  }

  init(data) {
    // Retrieve data from CharacterCreationScene (or saved data)
    this.chosenClass = data.chosenClass || "archer";
    console.log("Chosen Class:", this.chosenClass);

    // Receive gameId and attributes if provided.
    this.gameId = data.gameId || "";
    this.attributes = data.attributes || {
      Strength: 0,
      Dexterity: 0,
      Agility: 0,
      Wisdom: 0,
      Stamina: 0
    };

    // Level and available points
    this.level = data.level || 1;
    this.availablePoints = data.availablePoints || 0;

    // Save player's last position if provided
    this.savedPosition = data.position || null;
  }

  preload() {
    // Load character idle spritesheets (each is 600x100 with 6 frames)
    this.load.spritesheet("archer-idle", "/assets/chars/archer/Archer-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });
    this.load.spritesheet("knight-idle", "/assets/chars/knight/Knight-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });
    this.load.spritesheet("orc-idle", "/assets/chars/orc/Orc-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });
    this.load.spritesheet("priest-idle", "/assets/chars/priest/Priest-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });
    this.load.spritesheet("swordsman-idle", "/assets/chars/swordsman/Swordsman-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });
    this.load.spritesheet("wizard-idle", "/assets/chars/wizard/Wizard-Idle.png", {
      frameWidth: 100,
      frameHeight: 100
    });

    // Load Tiled map and tileset image.
    this.load.image("tiles", "/assets/tilesets/fantasyhextiles_v3.png");
    this.load.tilemapTiledJSON("map", "/assets/maps/OnlineGame.tmj");

    // Load background music.
    // (Replace '/assets/music/bg-music.mp3' with your public-domain track file.)
    this.load.audio("bgMusic", "/assets/music/bg-music.mp3");
  }

  create() {
    // --- Map Setup ---
    const map = this.make.tilemap({ key: "map" });
    const tileset = map.addTilesetImage("fantasyhextiles_v3", "tiles");
    map.createLayer("Tile Layer 1", tileset, 0, 0).setDepth(0);
    map.createLayer("Tile Layer 2", tileset, 0, 0).setDepth(0);

    // --- Animation Setup ---
    const classes = ["archer", "knight", "orc", "priest", "swordsman", "wizard"];
    classes.forEach(cls => {
      if (!this.anims.exists(`${cls}-idle-anim`)) {
        this.anims.create({
          key: `${cls}-idle-anim`,
          frames: this.anims.generateFrameNumbers(`${cls}-idle`, { start: 0, end: 5 }),
          frameRate: 6,
          repeat: -1
        });
      }
      console.log(`Ensured animation for ${cls}-idle-anim:`, this.anims.get(`${cls}-idle-anim`));
    });

    // --- Player Setup ---
    const textureKey = `${this.chosenClass}-idle`;
    console.log("Using textureKey:", textureKey);
    this.player = this.physics.add.sprite(200, 200, textureKey).setScale(1.5);
    console.log("Player sprite created:", this.player);
    const animKey = `${this.chosenClass}-idle-anim`;
    if (this.anims.get(animKey)) {
      this.player.play(animKey);
      console.log(`Playing animation: ${animKey}`);
    } else {
      console.warn(`Animation "${animKey}" not found.`);
    }
    // If a saved position exists, move the player there.
    if (this.savedPosition) {
      this.player.setPosition(this.savedPosition.x, this.savedPosition.y);
      console.log("Player position set to saved position:", this.savedPosition);
    }

    // --- Camera Setup ---
    this.cameras.main.setBounds(0, 0, map.widthInPixels, map.heightInPixels);
    this.cameras.main.setZoom(0.8);
    this.cameras.main.startFollow(this.player);

    // --- Movement Input ---
    this.cursors = this.input.keyboard.createCursorKeys();
    this.playerSpeed = 100;

    // --- Background Music Setup ---
    this.bgMusic = this.sound.add("bgMusic", { volume: 0.5, loop: true });
    this.bgMusic.play();

    // --- Menu System Setup ---
    this.menuButton = this.add.text(780, 5, "Menu", {
      font: "20px monospace",
      fill: "#ffffff",
      backgroundColor: "#000000"
    })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setPadding(5)
      .setInteractive({ useHandCursor: true })
      .setDepth(10000);

    this.menuButton.on("pointerdown", () => {
      if (this.menuOptionsVisible) {
        this.hideMenuOptions();
      } else {
        this.showMenuOptions();
      }
    });

    this.menuOptions = [];
    this.menuOptionsVisible = false;
    const menuX = 780;
    const menuY = 35;
    const menuBg = this.add.rectangle(menuX, menuY, 160, 210, 0x000000, 0.8)
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(10001);
    menuBg.disableInteractive();
    this.menuOptions.push(menuBg);

    // Define menu options (including "Character", "Settings", "Backpack", "Friends", "Quest", and "Save")
    const menuOptionsData = ["Character", "Settings", "Backpack", "Friends", "Quest", "Save"];
    menuOptionsData.forEach((option, index) => {
      const optText = this.add.text(menuX - 10, menuY + 10 + index * 40, option, {
        font: "20px monospace",
        fill: "#ffff00"
      })
        .setOrigin(1, 0)
        .setScrollFactor(0)
        .setDepth(10002)
        .setInteractive({ useHandCursor: true });
      optText.on("pointerdown", () => {
        this.hideMenuOptions();
        if (option === "Character") {
          this.openCharacterProfile();
        } else if (option === "Backpack") {
          this.openBackpack();
        } else if (option === "Settings") {
          this.openSettings();
        } else if (option === "Save") {
          this.manualSave();
        }
        // Additional logic for "Friends" and "Quest" can be added here.
      });
      this.menuOptions.push(optText);
    });
    this.hideMenuOptions();

    // --- Overlays Setup ---
    this.backpackContainer = null;
    this.characterContainer = null;
    this.charOverlayTexts = [];

    // --- ESC Key Listener for Toggling Overlays ---
    this.input.keyboard.on("keydown", (event) => {
      if (event.key === "Escape") {
        if (this.characterContainer) {
          this.closeCharacterProfile();
          return;
        }
        if (this.backpackContainer) {
          this.closeBackpack();
        } else if (this.settingsContainer) {
          this.closeSettings();
        }
      }
    });

    // --- Auto-Save Setup ---
    this.time.addEvent({
      delay: 60000, // every 60 seconds
      callback: () => {
        this.saveProgress();
      },
      loop: true
    });
  }

  hideMenuOptions() {
    this.menuOptions.forEach(obj => {
      obj.visible = false;
    });
    this.menuOptionsVisible = false;
  }

  showMenuOptions() {
    this.menuOptions.forEach(obj => {
      obj.visible = true;
    });
    this.menuOptionsVisible = true;
  }

  // --- Auto-Save & Manual Save Functions ---
  saveProgress() {
    const characterData = {
      chosenClass: this.chosenClass,
      gameId: this.gameId,
      attributes: this.attributes,
      level: this.level,
      availablePoints: this.availablePoints,
      position: { x: this.player.x, y: this.player.y }
    };
    // Replace with your backend save function when ready.
    console.log("Auto-saving character progress:", characterData);
  }

  manualSave() {
    const characterData = {
      chosenClass: this.chosenClass,
      gameId: this.gameId,
      attributes: this.attributes,
      level: this.level,
      availablePoints: this.availablePoints,
      position: { x: this.player.x, y: this.player.y }
    };
    // Replace with your backend save function when ready.
    console.log("Manual save triggered:", characterData);
  }

  // --- Character Profile Overlay ---
  openCharacterProfile() {
    if (this.characterContainer) return; // Already open

    this.characterContainer = this.add.container(400, 300).setScrollFactor(0).setDepth(10003);
    const PROFILE_WIDTH = 700;
    const PROFILE_HEIGHT = 700;

    // Lighter-tone background
    const bg = this.add.rectangle(0, 0, PROFILE_WIDTH, PROFILE_HEIGHT, 0x666666, 0.9)
      .setOrigin(0.5);
    bg.disableInteractive();
    this.characterContainer.add(bg);

    // Title at the top
    const titleText = this.add.text(0, -PROFILE_HEIGHT / 2 + 10, "Character Profile", {
      font: "28px monospace",
      fill: "#ffff00"
    }).setOrigin(0.5, 0);
    this.characterContainer.add(titleText);
    this.charOverlayTexts.push(titleText);

    // --- Character Preview ---
    // Display the character idle sprite (using chosen class) inside the overlay.
    const spriteX = -PROFILE_WIDTH / 2 + 170;
    const spriteY = -PROFILE_HEIGHT / 2 + 250;
    const previewSprite = this.add.sprite(spriteX, spriteY, `${this.chosenClass}-idle`).setScale(7);
    const animKey = `${this.chosenClass}-idle-anim`;
    if (!this.anims.exists(animKey)) {
      this.anims.create({
        key: animKey,
        frames: this.anims.generateFrameNumbers(`${this.chosenClass}-idle`, { start: 0, end: 5 }),
        frameRate: 6,
        repeat: -1
      });
    }
    previewSprite.play(animKey);
    this.characterContainer.add(previewSprite);

    // --- Equipment Slots Around the Sprite ---
    const eqSlots = [
      { label: "Helmet",    x: spriteX,         y: spriteY - 100 },
      { label: "Armour",    x: spriteX - 100,     y: spriteY },
      { label: "Weapon",    x: spriteX + 100,     y: spriteY },
      { label: "Ring",      x: spriteX - 100,     y: spriteY + 100 },
      { label: "Bracelet",  x: spriteX - 100,     y: spriteY + 170 },
      { label: "Gem 1",     x: spriteX + 100,     y: spriteY + 100 },
      { label: "Gem 2",     x: spriteX + 100,     y: spriteY + 170 }
    ];
    eqSlots.forEach(slot => {
      const eqBox = this.add.rectangle(slot.x, slot.y, 60, 60, 0xffffff, 0.2)
        .setStrokeStyle(2, 0xffffff);
      this.characterContainer.add(eqBox);
      const eqLabel = this.add.text(slot.x, slot.y, slot.label, {
        font: "12px monospace",
        fill: "#ffffff"
      }).setOrigin(0.5);
      this.characterContainer.add(eqLabel);
    });

    // --- Right Side: Character Info and Attribute Redistribution ---
    // Define a text column for character info.
    const textColumnX = -PROFILE_WIDTH / 2 + 400;
    let lineY = -PROFILE_HEIGHT / 2 + 50;

    // Game ID
    const idText = this.add.text(textColumnX, lineY, `Game ID: ${this.gameId}`, {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0, 0);
    this.characterContainer.add(idText);
    this.charOverlayTexts.push(idText);
    lineY += 40;

    // Class
    const classText = this.add.text(textColumnX, lineY, `Class: ${this.chosenClass}`, {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0, 0);
    this.characterContainer.add(classText);
    this.charOverlayTexts.push(classText);
    lineY += 40;

    // Level
    const levelText = this.add.text(textColumnX, lineY, `Level: ${this.level}`, {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0, 0);
    this.characterContainer.add(levelText);
    this.charOverlayTexts.push(levelText);
    lineY += 40;

    // Points Left
    this.pointsLeftText = this.add.text(textColumnX, lineY, `Points Left: ${this.availablePoints}`, {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0, 0);
    this.characterContainer.add(this.pointsLeftText);
    this.charOverlayTexts.push(this.pointsLeftText);
    lineY += 40;

    // Attributes with plus/minus buttons.
    for (const attr in this.attributes) {
      // Attribute label.
      const attrLabel = this.add.text(textColumnX, lineY, `${attr}:`, {
        font: "24px monospace",
        fill: "#ffff00"
      }).setOrigin(0, 0);
      this.characterContainer.add(attrLabel);
      this.charOverlayTexts.push(attrLabel);
      lineY += 30;

      // Attribute value.
      const attrValueText = this.add.text(textColumnX, lineY, this.attributes[attr].toString(), {
        font: "24px monospace",
        fill: "#ffffff"
      }).setOrigin(0, 0);
      this.characterContainer.add(attrValueText);
      this.charOverlayTexts.push(attrValueText);

      // Minus button.
      const minusButton = this.add.text(textColumnX - 50, lineY + 10, "-", {
        font: "28px monospace",
        fill: "#ff0000"
      })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      this.characterContainer.add(minusButton);
      this.charOverlayTexts.push(minusButton);
      minusButton.on("pointerdown", () => {
        if (this.attributes[attr] > 0) {
          this.attributes[attr]--;
          attrValueText.setText(this.attributes[attr]);
          this.availablePoints++;
          this.pointsLeftText.setText(`Points Left: ${this.availablePoints}`);
        }
      });

      // Plus button.
      const plusButton = this.add.text(textColumnX + 50, lineY + 10, "+", {
        font: "28px monospace",
        fill: "#00ff00"
      })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      this.characterContainer.add(plusButton);
      this.charOverlayTexts.push(plusButton);
      plusButton.on("pointerdown", () => {
        if (this.availablePoints > 0) {
          this.attributes[attr]++;
          attrValueText.setText(this.attributes[attr]);
          this.availablePoints--;
          this.pointsLeftText.setText(`Points Left: ${this.availablePoints}`);
        }
      });

      lineY += 40;
    }
  }

  closeCharacterProfile() {
    if (this.characterContainer) {
      this.characterContainer.destroy();
      this.characterContainer = null;
    }
    this.charOverlayTexts.forEach(txt => txt.destroy());
    this.charOverlayTexts = [];
  }

  // --- Backpack Overlay ---
  closeBackpack() {
    if (this.backpackContainer) {
      this.backpackContainer.destroy();
      this.backpackContainer = null;
    }
    if (this.backpackCloseButton) {
      this.backpackCloseButton.destroy();
      this.backpackCloseButton = null;
    }
  }

  openBackpack() {
    if (this.backpackContainer) return;
    this.backpackContainer = this.add.container(400, 300).setScrollFactor(0).setDepth(10003);
    const BACKPACK_WIDTH = 440;
    const BACKPACK_HEIGHT = 440;
    const bg = this.add.rectangle(0, 0, BACKPACK_WIDTH, BACKPACK_HEIGHT, 0x000000, 0.9)
      .setOrigin(0.5);
    bg.disableInteractive();
    this.backpackContainer.add(bg);
    const titleText = this.add.text(0, -BACKPACK_HEIGHT / 2 + 5, "Backpack", {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0.5, 0);
    this.backpackContainer.add(titleText);
    const titlePadding = 80;
    const gridTop = -BACKPACK_HEIGHT / 2 + titlePadding;
    const rows = 6;
    const cols = 6;
    const slotSize = 50;
    const spacing = 10;
    const gridWidth = cols * slotSize + (cols - 1) * spacing;
    const startX = -gridWidth / 2;
    const startY = gridTop;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const x = startX + c * (slotSize + spacing);
        const y = startY + r * (slotSize + spacing);
        const slot = this.add.rectangle(x, y, slotSize, slotSize, 0xffffff, 0.2)
          .setStrokeStyle(2, 0xffffff);
        this.backpackContainer.add(slot);
      }
    }
  }

  // --- Settings Overlay (for music volume control) ---
  openSettings() {
    if (this.settingsContainer) return;
  
    // Create a container at a fixed screen position (center of the screen).
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    this.settingsContainer = this.add.container(centerX, centerY)
      .setScrollFactor(0)
      .setDepth(10003);
  
  
    // Add a background (non-interactive) to the container.
    const SETTINGS_WIDTH = 400;
    const SETTINGS_HEIGHT = 300;
    const bg = this.add.rectangle(0, 0, SETTINGS_WIDTH, SETTINGS_HEIGHT, 0x222222, 0.9)
      .setOrigin(0.5);
    bg.disableInteractive();
    this.settingsContainer.add(bg);
  
    // Title text.
    const titleText = this.add.text(0, -SETTINGS_HEIGHT / 2 + 10, "Settings", {
      font: "28px monospace",
      fill: "#ffff00"
    }).setOrigin(0.5, 0);
    this.settingsContainer.add(titleText);
  
    // Music Volume label.
    const volLabel = this.add.text(-100, -50, "Music Volume:", {
      font: "24px monospace",
      fill: "#ffffff"
    }).setOrigin(0, 0.5);
    this.settingsContainer.add(volLabel);
  
    // Create a Phaser-based slider.
    // Its position is relative to the container, so it stays in place.
    this.volumeSlider = new PhaserSlider(this, 50, -50, 150, this.bgMusic.volume, (value) => {
      this.bgMusic.setVolume(value);
      console.log("Music volume set to:", value);
    });
    this.settingsContainer.add(this.volumeSlider);
  }
  
  closeSettings() {
    if (this.settingsContainer) {
      this.settingsContainer.destroy();
      this.settingsContainer = null;
    }
    if (this.volumeSlider) {
      this.volumeSlider.destroy();
      this.volumeSlider = null;
    }
  }

  update(time, delta) {
    this.player.setVelocity(0);
    if (this.cursors.left.isDown) {
      this.player.setVelocityX(-this.playerSpeed);
      this.player.setFlipX(true);
    } else if (this.cursors.right.isDown) {
      this.player.setVelocityX(this.playerSpeed);
      this.player.setFlipX(false);
    }
    if (this.cursors.up.isDown) {
      this.player.setVelocityY(-this.playerSpeed);
    } else if (this.cursors.down.isDown) {
      this.player.setVelocityY(this.playerSpeed);
    }
  }

}